import { useCallback, useEffect, useState } from "react";
import useApiHook from "../../../utils/useApi";
import DataTable, { type TableColumn } from "react-data-table-component";
import Loading from "../../../merchant_dashboard/components/loading/Loading";
import { customStyles_T } from "../../../utils/table_utils";
import type {
  ApiBankTransfersResponse,
  BankTransfersData,
} from "../../../interface/interfaces";
import FilterComponentt from "./FilterComponent";
import { Eye, Download } from "lucide-react";
import BankTransferModal from "./BankTransferModal";

const BankTransfers = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [statusQuery, setStatusQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [debouncedStatus, setDebouncedStatus] = useState<string>("");
  const [selectedTransaction, setSelectedTransaction] =
    useState<BankTransfersData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { data, isLoading, makeRequest } = useApiHook<ApiBankTransfersResponse>(
    "/thirdparties/banktransfers",
    {
      method: "get",
    }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStatus(statusQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [statusQuery]);

  const fetchTransactions = useCallback(async () => {
    const params = {
      page,
      size: perPage,
      search: debouncedSearch.trim() || "",
      status: debouncedStatus.trim() || "",
    };

    const response = await makeRequest(params);

    if (response?.data?.statusCode !== "00") {
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage, debouncedSearch, debouncedStatus]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPage(page);
  };

  // Handle rows per page change
  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format amount
  const formatAmount = (amount: number, currency: string) => {
    return `${currency} ${amount?.toLocaleString() || 0}`;
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "PAID":
        return "bg-green-100 text-green-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Open modal with transaction details
  const handleViewMore = (transaction: BankTransfersData) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  // Export to CSV
  const exportToCSV = () => {
    const transactions = data?.data?.data || [];
    if (transactions.length === 0) return;

    const headers = [
      "Reference",
      "Customer ID",
      "Amount",
      "Currency",
      "Request ID",
      "Status",
      "Date Created",
      "Date Updated",
      "Merchant ID",
    ];

    const csvContent = [
      headers.join(","),
      ...transactions.map((transaction) =>
        [
          transaction.reference,
          transaction.customerId,
          transaction.amount,
          transaction.currency,
          transaction.requestId,
          transaction.status,
          transaction.dateCreated,
          transaction.dateUpdated || "N/A",
          transaction.merchantId,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `bank_transfers_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns: TableColumn<BankTransfersData>[] = [
    {
      name: "Reference",
      selector: (row) => row.reference,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">{row.reference}</div>
      ),
    },
    {
      name: "Customer ID",
      selector: (row) => row.customerId,
      sortable: true,
      cell: (row) => <div className="text-gray-700">{row.customerId}</div>,
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <div className="font-medium text-gray-900">
          {formatAmount(row.amount, row.currency)}
        </div>
      ),
    },
    {
      name: "Request ID",
      selector: (row) => row.requestId,
      sortable: true,
      cell: (row) => (
        <div className="text-gray-700 font-mono text-sm">{row.requestId}</div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            row.status
          )}`}
        >
          {row.status}
        </span>
      ),
    },
    {
      name: "Date Created",
      selector: (row) => row.dateCreated,
      sortable: true,
      cell: (row) => (
        <div className="text-gray-700 text-sm">
          {formatDate(row.dateCreated)}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          onClick={() => handleViewMore(row)}
          className="flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors duration-200"
        >
          <Eye size={16} />
          <span className="text-sm">View More</span>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusQuery(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusQuery("");
    setPage(1);
  };

  return (
    <section>
      <div className="flex justify-between items-center mb-4">
        <div className="flex-1">
          <FilterComponentt
            searchQuery={searchQuery}
            statusQuery={statusQuery}
            onSearchChange={handleSearchChange}
            onStatusChange={handleStatusChange}
            onClearFilters={handleClearFilters}
          />
        </div>
        <button
          onClick={exportToCSV}
          disabled={!data?.data?.data || data.data.data.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
        >
          <Download size={16} />
          Export CSV
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data?.data?.data || []}
        progressPending={isLoading}
        progressComponent={<Loading />}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[10, 20, 30, 50]}
        highlightOnHover
        responsive
        persistTableHead
        customStyles={customStyles_T}
        noDataComponent={
          <div className="p-6 text-center text-gray-500">
            No transactions found
          </div>
        }
      />

      {/* Modal for transaction details */}
      <BankTransferModal
        isOpen={isModalOpen}
        onClose={closeModal}
        transaction={selectedTransaction}
        formatDate={formatDate}
        formatAmount={formatAmount}
        getStatusColor={getStatusColor}
      />
    </section>
  );
};

export default BankTransfers;
